import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { FormTextInput } from '@tanium/react-form-text-input';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormRow } from '@tanium/react-form';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { CheckIcon, CloseIcon, EditIcon } from '@tanium/react-graphics';
import { MediumSubtleText } from '../../TextStyles';
import { ConfigureIdpRequest } from '../../models/IdpModels';

export const RenderEmailAttribute: React.VFC = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [originalValue, setOriginalValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const onEnableEdit = useCallback(
    (form: FormikProps<ConfigureIdpRequest>) => {
      setIsEditing(true);
      setOriginalValue(form.values.emailAttributeName);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    },
    [setIsEditing, inputRef]
  );

  const onSave = (form: FormikProps<ConfigureIdpRequest>) => {
    setIsEditing(false);
    setOriginalValue(form.values.emailAttributeName);
  };

  const onCancel = useCallback(
    (form: FormikProps<ConfigureIdpRequest>) => {
      setIsEditing(false);
      void form.setFieldValue('emailAttributeName', originalValue, true);
    },
    [originalValue]
  );

  return (
    <Field name="emailAttributeName">
      {({ form }: FieldProps<string, ConfigureIdpRequest>): React.ReactElement => (
        <FormRow
          dataKey="email-attribute"
          label={
            <MediumSubtleText>
              {t(
                'Configure your identity provider to send the email address as the following attribute or specify the attribute containing the email address.'
              )}
            </MediumSubtleText>
          }
          errors={form.errors?.emailAttributeName}
          required
        >
          <Box>
            <Stack direction="column" itemSpacing={1}>
              <Box>
                <Stack direction="row" alignItems="center" itemSpacing={2}>
                  <Box>
                    <FormTextInput
                      data-testid="editable-value-input"
                      style={{ width: '600px' }}
                      value={form.values.emailAttributeName}
                      onChange={(e) => form.setFieldValue('emailAttributeName', e.target.value, true)}
                      disabled={!isEditing}
                    />
                  </Box>
                  {isEditing ? (
                    <>
                      <TooltippedIconButton
                        label={t('Save')}
                        tooltipContent={t('Apply the updated value')}
                        height={16}
                        width={16}
                        icon={CheckIcon}
                        onClick={() => onSave(form)}
                      />
                      &nbsp;
                      <TooltippedIconButton
                        label={t('Cancel')}
                        tooltipContent={t('Cancel changes')}
                        height={16}
                        width={16}
                        icon={CloseIcon}
                        onClick={() => onCancel(form)}
                      />
                    </>
                  ) : (
                    <TooltippedIconButton
                      label={t('Edit')}
                      height={16}
                      width={16}
                      icon={EditIcon}
                      tooltipContent={t('Edit')}
                      onClick={() => onEnableEdit(form)}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </FormRow>
      )}
    </Field>
  );
};
